import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import toast from 'react-hot-toast';
import { getBlogPosts, getBlogCategories, subscribeNewsletter } from '../services/firebaseService';
import { FadeInUp, FadeIn } from '../components/Animations';
import { isValidEmail, checkRateLimit } from '../utils/validation';

const POSTS_PER_PAGE = 6;

export default function Blog() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [searchParams, setSearchParams] = useSearchParams();

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('cat') || '');
  const [nlEmail, setNlEmail] = useState('');

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBlogPosts(activeCategory, search);
      setPosts(data);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, search]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    getBlogCategories().then(setCategories).catch(() => {});
  }, []);

  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);
  const displayed = posts.slice((page - 1) * POSTS_PER_PAGE, page * POSTS_PER_PAGE);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = e.target.elements.q.value.trim();
    setSearch(q);
    setPage(1);
    const params = {};
    if (q) params.q = q;
    if (activeCategory) params.cat = activeCategory;
    setSearchParams(params);
  };

  const handleCategoryClick = (cat) => {
    const next = cat === activeCategory ? '' : cat;
    setActiveCategory(next);
    setPage(1);
    const params = {};
    if (search) params.q = search;
    if (next) params.cat = next;
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setActiveCategory('');
    setPage(1);
    setSearchParams({});
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!nlEmail || !isValidEmail(nlEmail)) { toast.error(t('footer.enterEmail')); return; }
    if (!checkRateLimit('blog-newsletter', 3, 60000)) {
      toast.error(t('contactPage.tooManyAttempts') || 'Too many attempts. Please wait.');
      return;
    }
    try {
      const result = await subscribeNewsletter(nlEmail);
      if (result.alreadySubscribed) toast(t('footer.alreadySubscribed'), { icon: 'ℹ️' });
      else toast.success(t('footer.subscribeSuccess'));
      setNlEmail('');
    } catch {
      toast.error(t('contactPage.errorMsg'));
    }
  };

  const placeholder = (title) => {
    const colors = ['#542DB7', '#1D5962', '#EF4618', '#2d2d6b', '#1a5940'];
    const hash = title.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return `linear-gradient(135deg, ${colors[hash % colors.length]}, ${colors[(hash + 2) % colors.length]})`;
  };

  const getTitle = (post) => isRTL && post.titleAr ? post.titleAr : post.title;
  const getExcerpt = (post) => isRTL && post.excerptAr ? post.excerptAr : post.excerpt;

  return (
    <>
      <SEO
        title={isRTL ? 'وجود لينك | المدونة' : 'Wojod Link | Blog'}
        description={t('blogPage.description')}
      />

      {/* Page Hero */}
      <section className="page-hero">
        <div className="container">
          <FadeInUp>
            <div className="page-hero-content">
              <span className="section-label">{t('blogPage.label')}</span>
              <h1 className="page-title">{t('blogPage.title')}</h1>
              <p className="page-description">{t('blogPage.description')}</p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Blog Section */}
      <section className="blog-section">
        <div className="container">
          {/* Toolbar */}
          <FadeInUp>
            <div className="blog-toolbar">
              <form className="blog-search-form" onSubmit={handleSearch}>
                <div className="search-input-wrapper">
                  <i className="fas fa-search" />
                  <input
                    type="text"
                    name="q"
                    className="blog-search-input"
                    placeholder={t('blogPage.searchPlaceholder')}
                    defaultValue={search}
                  />
                </div>
                <button type="submit" className="btn btn-primary" style={{ padding: '10px 24px' }}>
                  {t('blogPage.search')}
                </button>
              </form>

              <div className="blog-categories">
                <button
                  className={`category-tag${!activeCategory ? ' active' : ''}`}
                  onClick={() => handleCategoryClick('')}
                >
                  {t('blogPage.all')}
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`category-tag${activeCategory === cat.name ? ' active' : ''}`}
                    onClick={() => handleCategoryClick(cat.name)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </FadeInUp>

          {/* Active filters info */}
          {(search || activeCategory) && (
            <div className="search-results-info">
              {search && <span>{t('blogPage.searchResultsFor')} <strong>{search}</strong> </span>}
              {activeCategory && <span>{t('blogPage.inCategory')} <strong>{activeCategory}</strong> </span>}
              <span>({posts.length} {t('blogPage.articles')})</span>
              <button className="clear-filters" onClick={clearFilters}>
                {t('blogPage.clearFilters')}
              </button>
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div className="loading-container"><div className="spinner" /></div>
          ) : displayed.length > 0 ? (
            <div className="blog-grid">
              {displayed.map((post, i) => (
                <FadeInUp key={post.id} delay={i * 0.08}>
                  <Link to={`/blog/${post.slug}`} style={{ display: 'block', height: '100%' }}>
                    <div className="blog-card" style={{ height: '100%' }}>
                      <div className="blog-image">
                        {post.image ? (
                          <img src={post.image} alt={getTitle(post)} loading="lazy" />
                        ) : (
                          <div style={{ width: '100%', height: '100%', background: placeholder(post.title), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fas fa-feather-alt" style={{ fontSize: '2.5rem', color: 'rgba(255,255,255,0.2)' }} />
                          </div>
                        )}
                        {post.category && (
                          <span className="blog-category-badge">{post.category}</span>
                        )}
                      </div>
                      <div className="blog-content">
                        <div className="blog-meta">
                          <span><i className="far fa-calendar" /> {post.date}</span>
                          {post.readTime && <span><i className="far fa-clock" /> {post.readTime} {t('blogPage.minRead')}</span>}
                        </div>
                        <h3 className="blog-title">{getTitle(post)}</h3>
                        <p className="blog-excerpt">{getExcerpt(post)}</p>
                        <span className="blog-link">
                          {t('blogPage.readMore')} <i className={`fas fa-arrow-${isRTL ? 'left' : 'right'}`} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </FadeInUp>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <i className="fas fa-newspaper" />
              <p>{search || activeCategory ? t('blogPage.noResults') : t('blogPage.noArticles')}</p>
              {(search || activeCategory) && (
                <button className="btn btn-outline" onClick={clearFilters}>
                  {t('blogPage.showAll')}
                </button>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <FadeIn>
              <div className="pagination">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    className={`page-link${p === page ? ' active' : ''}`}
                    onClick={() => { setPage(p); window.scrollTo({ top: 300, behavior: 'smooth' }); }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </FadeIn>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="newsletter-section">
        <div className="container">
          <FadeInUp>
            <div className="newsletter-content">
              <div className="newsletter-text">
                <h2>{t('blogPage.newsletterTitle')}</h2>
                <p>{t('blogPage.newsletterDesc')}</p>
              </div>
              <form className="newsletter-form" onSubmit={handleNewsletter}>
                <input
                  type="email"
                  placeholder={t('blogPage.emailPlaceholder')}
                  value={nlEmail}
                  onChange={(e) => setNlEmail(e.target.value)}
                  required
                />
                <button type="submit" className="btn btn-primary">
                  {t('blogPage.subscribe')}
                </button>
              </form>
            </div>
          </FadeInUp>
        </div>
      </section>
    </>
  );
}
